import { createTheme, type MantineColorsTuple } from '@mantine/core';
import type { OrderStatus } from '@blackarrow/types';

/**
 * Mantine theme derived from tokens.css.
 *
 * tokens.css is the source of truth for colour. This file exists so Mantine
 * components inherit the same values instead of falling back to Mantine's
 * defaults — which is what makes a UI look half-designed.
 *
 * If you need a new colour, add it to tokens.css first, then surface it here.
 */

// Ink ramp (2026-10-21 brand refresh). Index 6 is the exact brand ink
// #1D1D1F; the rest step through the Language board's greys. Mantine reads
// this ramp for Button/Badge/Tabs/etc. with primaryColor, and in light mode
// shade 6 is the fill, 7 the hover.
const accent: MantineColorsTuple = [
  '#f5f5f7', // --ba-tile
  '#e8e8ed', // --ba-accent-tint
  '#d2d2d7', // --ba-line
  '#a1a1a6',
  '#86868b', // --ba-accent-light
  '#6e6e73', // --ba-muted
  '#1d1d1f', // --ba-accent  (the brand ink)
  '#424245', // --ba-accent-dark (hover)
  '#2c2c2e',
  '#000000', // --ba-accent-darkest
];

const neutral: MantineColorsTuple = [
  '#ffffff', // --ba-paper (app background)
  '#f5f5f7', // --ba-tile (table headers, stat/money tiles)
  '#ececf0',
  '#e8e8ed', // --ba-line-2
  '#d2d2d7', // --ba-line
  '#a1a1a6',
  '#767679', // --ba-muted-2
  '#6e6e73', // --ba-muted
  '#424245',
  '#1d1d1f', // --ba-ink
];


// Mantine's own colour names, remapped so a `color="red"` prop reads as the
// board's status colours instead of Mantine's bright defaults. Index 6 is the
// fill / text shade and matches --ba-danger, --ba-warning, --ba-success and
// --ba-info; index 0 is the tint. Purple-family names map to the ink ramp, so
// no prop can bring purple back.
const red: MantineColorsTuple = [
  '#fde9eb', '#fbd3d8', '#f5a9b2', '#ee7d8b', '#e75566',
  '#d82c43', '#b3001b', '#9a0017', '#820013', '#6a000f',
];
const orange: MantineColorsTuple = [
  '#fdf1e1', '#fae1c2', '#f4c288', '#eca24c', '#e48a1e',
  '#c46a00', '#8a4500', '#753a00', '#603000', '#4b2500',
];
const green: MantineColorsTuple = [
  '#e7f4ea', '#cfe9d6', '#a1d3ae', '#70bc85', '#4aa863',
  '#2d8a4a', '#1e6b34', '#195b2d', '#144a25', '#0f391d',
];
const blue: MantineColorsTuple = [
  '#e8f1fb', '#d0e3f7', '#a2c7ef', '#71aae6', '#4a92de',
  '#1c73c9', '#0050a3', '#00458a', '#003a73', '#002f5c',
];
const gray: MantineColorsTuple = [
  '#f5f5f7', '#e8e8ed', '#d2d2d7', '#a1a1a6', '#86868b',
  '#767679', '#6e6e73', '#424245', '#2c2c2e', '#1d1d1f',
];

export const theme = createTheme({
  colors: {
    accent,
    neutral,
    red,
    orange,
    green,
    teal: green,
    blue,
    gray,
    grape: accent,
    violet: accent,
    purple: accent,
    indigo: accent,
  },
  primaryColor: 'accent',
  primaryShade: 6,

  white: '#ffffff',
  black: '#1d1d1f',

  fontFamily: 'var(--ba-font-body)',
  fontFamilyMonospace: 'var(--ba-font-mono)',
  headings: { fontFamily: 'var(--ba-font-display)' },

  defaultRadius: 'md',
  cursorType: 'pointer',
  radius: {
    sm: '6px',
    md: 'var(--ba-radius-control)', // 12px — inputs, selects
    lg: 'var(--ba-radius)', // 28px — cards, panels
    xl: 'var(--ba-radius-pill)', // 999px — buttons, badges
  },

  shadows: {
    sm: 'var(--ba-shadow)',
    md: 'var(--ba-shadow)',
    lg: 'var(--ba-shadow-accent)',
  },

  components: {
    // Every button is a full pill. Primary is ink on white (inverted in dark
    // mode through --ba-accent / --ba-on-accent), secondary a hairline outline.
    Button: {
      defaultProps: { radius: 'xl' },
      styles: {
        root: { fontSize: 'var(--ba-text-control)', fontWeight: 600 },
      },
    },
    // Badges are pills with a dot-less tint; Mantine's own colour names
    // (red, orange, teal, ...) resolve through its palette, which is left
    // alone here — the colour-prop audit is a later slice.
    Badge: {
      defaultProps: { radius: 'xl' },
      styles: {
        root: { fontWeight: 500, textTransform: 'none' },
      },
    },
    // Mantine paints modal and drawer surfaces with --mantine-color-body,
    // which the bridge points at --ba-paper. In dark mode that is #000, the
    // same as the page behind the overlay, so the dialog had no visible edge.
    // Lift them to the card surface with a hairline, the way Card does.
    Modal: {
      defaultProps: { radius: 'lg' },
      styles: {
        content: {
          backgroundColor: 'var(--ba-card)',
          border: '1px solid var(--ba-line)',
        },
        header: { backgroundColor: 'var(--ba-card)' },
      },
    },
    Drawer: {
      styles: {
        content: {
          backgroundColor: 'var(--ba-card)',
          border: '1px solid var(--ba-line)',
        },
        header: { backgroundColor: 'var(--ba-card)' },
      },
    },
    TextInput: {
      defaultProps: { radius: 'md' },
    },
    Select: {
      defaultProps: { radius: 'md' },
    },
    Tabs: {
      styles: {
        tab: { fontWeight: 500 },
      },
    },
    Table: {
      defaultProps: {
        highlightOnHover: true,
        verticalSpacing: 'sm',
      },
      styles: {
        table: { fontSize: 'var(--ba-text-table)' },
      },
    },
    Card: {
      defaultProps: { radius: 'lg', withBorder: true },
    },
  },
});

/**
 * Status pill colours.
 *
 * Deliberately the ONLY place a status maps to a colour. Doing this inline in
 * components is how two screens end up disagreeing about what "declined" looks
 * like.
 *
 * 2026-10-21 brand refresh: the Language board's chip row is the reference —
 * New is neutral grey, Ready and On the way are blue, Delivered green,
 * Rescheduled amber, Refused red, Archived grey. Statuses the board doesn't
 * show keep their previous tone, moved to the new palette:
 * out_for_delivery amber -> blue (On the way), new amber -> grey, rescheduled
 * red -> amber. Check the board before changing any of these.
 */
export const statusTone = {
  new: 'neutral',
  ready_for_pickup: 'info',
  picked_up: 'info',
  arrived_at_hub: 'info',
  out_for_delivery: 'info',
  delivered: 'success',
  declined: 'danger',
  rescheduled: 'warning',
  on_hold: 'warning', // not on the board; grouped with the other waits
  returned_to_hub: 'info',
  cash_reconciled: 'success',
  remitted: 'success',
  // Merchant-declined stays a distinct tone from the neutral ones: a solid
  // ink pill (see pillStyles.accent). One of the few places the design
  // differentiates; don't flatten it.
  declined_by_merchant: 'accent',
  // A refusal in a rider's bag: still moving, not yet settled — amber.
  declined_on_way_to_merchant: 'warning',
  declined_returned_to_merchant: 'neutral',
  // No prototype entry (added after the pilot design freeze) — danger, same
  // as declined: it's the worst outcome a parcel can have.
  lost: 'danger',
  // Never handed over, closed overnight after 7 days (ADR 0047). Nothing
  // went wrong in our hands, so grey rather than red.
  archived: 'neutral',
} as const satisfies Record<OrderStatus, PillTone>;

export type PillTone =
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral';

export const pillStyles: Record<PillTone, { bg: string; fg: string }> = {
  accent: { bg: 'var(--ba-accent)', fg: 'var(--ba-on-accent)' },
  success: { bg: 'var(--ba-success-tint)', fg: 'var(--ba-success)' },
  warning: { bg: 'var(--ba-warning-tint)', fg: 'var(--ba-warning)' },
  danger: { bg: 'var(--ba-danger-tint)', fg: 'var(--ba-danger)' },
  info: { bg: 'var(--ba-info-tint)', fg: 'var(--ba-info)' },
  neutral: { bg: 'var(--ba-neutral-tint)', fg: 'var(--ba-neutral)' },
};
